import * as React from "react";
import {
  Column,
  createColumnHelper,
  getCoreRowModel,
  RowData,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import Button from "react-bootstrap/Button";
import { EnvelopeFill } from "react-bootstrap-icons";
import {
  Link,
  LoaderFunction,
  Outlet,
  useLoaderData,
  useParams,
} from "react-router-dom";
import { Auth } from "aws-amplify";
import { getPagedPurchaseOrderItemsByVendorId } from "../../../_gateway/vendorGateway";
import {
  FilterOptions,
  PagingSortingFilteringParams,
  VendorPurchaseOrderItem,
  VendorPurchaseOrderItemsPaged,
} from "../../../_types";
import {
  BootstrapTable,
  CustomTablePagination,
  ExpandToggleIcon,
  PurchaseOrderItemPriorityField,
} from "../../../_components";
import {
  formatDate,
  getFilteringParams,
  getPagingParams,
  getSortingParams,
  nameofFactory,
} from "../../../_helpers";
import {
  useQueryStringFiltering,
  useQueryStringPagination,
  useQueryStringSingleSort,
} from "../../../_hooks";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends RowData, TValue> {
    filterRenderer: (column: Column<TData, TValue>) => JSX.Element;
    filterOptions?: FilterOptions;
  }
}

export const loader: LoaderFunction = async ({ request, params }) => {
  try {
    const currUser = await Auth.currentAuthenticatedUser();
    if (!currUser) return null;
  } catch (e) {
    return null;
  }

  const pagingParams = getPagingParams(request);
  const sortingParams = getSortingParams(request);
  const filteringParams = getFilteringParams(request);
  const { vendorId } = params;

  if (!vendorId) {
    return {} as PagingSortingFilteringParams;
  }

  const poParams: PagingSortingFilteringParams = {
    ...pagingParams,
    ...sortingParams,
    ...filteringParams,
  };

  const response = await getPagedPurchaseOrderItemsByVendorId(
    vendorId,
    poParams
  );
  return response;
};

export function VendorPoItemsPage() {
  const { onFilterChange, getFilterState } = useQueryStringFiltering();
  const { onPaginationChange, getPaginationState } = useQueryStringPagination();
  const { onSortingChange, getSortingState } = useQueryStringSingleSort();
  const { purchaseOrderItemId } = useParams();
  const data = useLoaderData() as VendorPurchaseOrderItemsPaged;
  const nameof = nameofFactory<VendorPurchaseOrderItem>();

  const getExpandedState = (d: VendorPurchaseOrderItemsPaged) => {
    if (!purchaseOrderItemId) return undefined;

    return {
      [d.results.findIndex(
        (z) => z.purchaseOrderItemId === parseInt(purchaseOrderItemId, 10)
      )]: true,
    };
  };

  const columnHelper = createColumnHelper<VendorPurchaseOrderItem>();
  const columns = React.useMemo(
    () => [
      columnHelper.accessor((row) => row, {
        header: "",
        id: "Expand",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => (
          <ExpandToggleIcon
            isExpanded={info.row.getIsExpanded()}
            purchaseOrderItemId={info.getValue().purchaseOrderItemId}
          />
        ),
      }),
      columnHelper.accessor((row) => row.purchaseOrderNumber, {
        header: "Purchase Order #",
        id: nameof("purchaseOrderNumber"),
      }),
      columnHelper.accessor((row) => row.lineItemNumber, {
        header: "Line Item #",
        id: nameof("lineItemNumber"),
      }),
      columnHelper.accessor((row) => row, {
        header: "Priority",
        id: nameof("priority"),
        cell: (info) => (
          <PurchaseOrderItemPriorityField
            purchaseOrderItemId={info.getValue().purchaseOrderItemId}
            priority={info.getValue().priority}
            canChangePriority={data.canChangePriority}
          />
        ),
      }),
      columnHelper.accessor((row) => row.description, {
        header: "Description of Work",
        id: nameof("description"),
      }),
      columnHelper.accessor((row) => row, {
        header: "Qty",
        id: nameof("quantity"),
        enableColumnFilter: false,
        cell: (info) =>
          `${info.getValue().quantity} ${info.getValue().unitOfMeasure ?? ""}`,
      }),
      columnHelper.accessor((row) => row.certificationRequired, {
        header: "Cert Req.",
        id: nameof("certificationRequired"),
        cell: (info) => (info.getValue() ? "Y" : "N"),
      }),
      columnHelper.accessor((row) => row.originalDueDate, {
        header: "Original Due Date",
        id: nameof("originalDueDate"),
        enableColumnFilter: false, // need to figure out good way to filter based on dateOnly and strings
        cell: (info) => {
          const originalDueDateData = info.getValue();
          return originalDueDateData ? formatDate(originalDueDateData) : "";
        },
      }),
      columnHelper.accessor((row) => row.modifiedDueDate, {
        header: "Modified Due Date",
        id: nameof("modifiedDueDate"),
        enableColumnFilter: false, // need to figure out good way to filter based on dateOnly and strings
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor((row) => row.timesDueDateChanged, {
        header: "# Times Due Date Changed",
        id: nameof("timesDueDateChanged"),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row, {
        header: "Messages",
        id: "messages",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => (
          <Link
            to={`purchaseOrderItem/${
              info.getValue().purchaseOrderItemId
            }/messages`}
          >
            <Button variant="light">
              <EnvelopeFill />
            </Button>
          </Link>
        ),
      }),
    ],
    [columnHelper, nameof, data.canChangePriority]
  );

  const paginationState = getPaginationState();
  const options: TableOptions<VendorPurchaseOrderItem> = {
    columns,
    data: data.results,
    manualExpanding: true,
    enableFilters: false, // remove if/when we need filters again
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(data.total / paginationState.pageSize),
    state: {
      columnFilters: getFilterState(),
      expanded: getExpandedState(data),
      pagination: paginationState,
      sorting: getSortingState(),
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnFiltersChange: onFilterChange,
    onPaginationChange,
    onSortingChange,
  };
  const table = useReactTable(options);

  return (
    <>
      <BootstrapTable table={table} expandedRow={<Outlet />} />
      <CustomTablePagination table={table} />
    </>
  );
}
